import { QuizWithProctoringOptions } from "./assessments-page-helper";
import {
	EditQuizWrapper,
	EditQuizWrapperRef,
	ExemptedStudent,
	api,
	VerityApi,
	useMobileLayout,
	EditQuizFooter,
	useEditQuizStyles,
	useEditQuizButton,
	EditQuizModalSkeleton,
	EditQuizType,
} from "@verity/components";
import { useContext, useEffect, useRef, useState } from "react";
import { CoreModal, coreToast } from "@verity/components/core";
import { AssessmentInfo } from "@verity/api-client/types";
import i18n from "../../../i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { Prisma } from "@verity/database/prisma";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { PortalControllerContext } from "../../context";
import { Id } from "react-toastify";

interface EditAssessmentOptionsModalProps {
	onClose: () => void;
	assessment: { quizId: string };
	isFirstTimeThirdPartyCreation?: boolean;
	afterSave?: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
	quizType: EditQuizType;
}

export default function EditAssessmentOptionsModal(props: EditAssessmentOptionsModalProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	// const [saveOption, setSaveOption] = useState<number>(0); // it is used to notify
	const [quizInfo, setQuizInfo] = useState<AssessmentInfo | null>(null);
	const [enforcedProfilesOnly, setEnforcedProfilesOnly] = useState<boolean>(true);
	const quiz = props.assessment;
	const isMobileLayout = useMobileLayout();

	const childRef = useRef<EditQuizWrapperRef>(null);
	useEffect(() => {
		void (async () => {
			if (!quiz) {
				return;
			}
			setEnforcedProfilesOnly(await api.areProctoringProfilesEnforced());
			if (!lmsProvidedCourseId) {
				throw new Error(`context missing: lmsProvidedCourseId=${lmsProvidedCourseId}`);
			}
			setQuizInfo({
				lms,
				hostname: instituteIntegrationHostname,
				courseId: lmsProvidedCourseId,
				quizId: quiz.quizId,
			});
		})();
	}, []);

	const [isFinalPage, setIsFinalPage] = useState(false);
	const { buttonHandlers, backButton, closeButton, handleButtonChanges, nextButton, saveButton, saveProfileButton } =
		useEditQuizButton();

	//the exemptedstudentInfo that is used to upload
	const [uploadedExemptedStudentsMap, setUploadedExemptedStudentsMap] = useState<Map<number, ExemptedStudent>>(
		new Map(),
	);

	const [uploadedProfilesMap, setUploadedProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	function hasQuizInfo(quiz: unknown): quiz is QuizWithProctoringOptions {
		return (quiz as QuizWithProctoringOptions).quizInfo !== undefined;
	}

	const quizName = hasQuizInfo(quiz)
		? `${((quiz.quizInfo as Prisma.JsonObject) ?? {})["name"]?.toString() ?? "?"}`
		: undefined;

	const loadingToastId = useRef<Id>();

	const { getEditQuizModalProps } = useEditQuizStyles();

	return (
		<CoreModal
			{...getEditQuizModalProps(isMobileLayout)}
			open
			onClose={() => {
				childRef?.current?.closeButtonHandler();
			}}
			disableTopCloseButton={!closeButton.enabled}
			showTopCloseButton={!isMobileLayout}
			buttons={
				<EditQuizFooter
					slotProps={{
						back: {
							onClick: () => childRef?.current?.backButtonHandler(),
							disabled: !backButton.enabled,
							hide: backButton.hide,
						},
						confirm: {
							disabled: !saveButton.enabled || !closeButton.enabled,
							onClick: () => {
								childRef.current?.saveButtonHandler().catch(console.error);
							},
							hide: !isFinalPage,
						},
						next: {
							disabled: !nextButton.enabled,
							onClick: () => childRef.current?.nextButtonHandler(),
							hide: isFinalPage,
						},
						saveProfile: {
							disabled: !closeButton.enabled || !saveProfileButton.enabled,
							hide: enforcedProfilesOnly || !isFinalPage || saveProfileButton.hide,
							onClick: () => {
								childRef.current?.saveAsProfile();
							},
						},
					}}
				/>
			}
		>
			{quizInfo ? (
				<EditQuizWrapper
					quizName={quizName}
					quizInfo={quizInfo}
					onClose={props.onClose}
					onStartSaveOptions={() => {
						handleButtonChanges("close", { enabled: false });
						loadingToastId.current = coreToast.loading("Applying your proctor settings...", {
							containerId: PORTAL_TOAST_CONTAINER_ID,
						});
					}}
					onSaveOptions={(success, messages, options) => {
						handleButtonChanges("close", { enabled: true });
						if (success) {
							props.afterSave?.(options);
							props.onClose();
							coreToast.dismiss();
							coreToast.success(i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyMessage"), {
								autoClose: 2500,
							});
							loadingToastId.current = undefined;
						} else {
							coreToast.updateOrShow(
								loadingToastId.current,
								<>
									{i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyFailedMessage")}
									{messages ? ": " : "."}
									{messages?.map((message) => (
										<div key={message}>{message}</div>
									))}
								</>,
								"error",
								{ containerId: PORTAL_TOAST_CONTAINER_ID, autoClose: 2500 },
							);
							loadingToastId.current = undefined;
						}
					}}
					ref={childRef}
					setIsFinalPage={setIsFinalPage}
					forceThirdParty={(quizInfo?.quizInfo?.name as string | undefined)?.startsWith("Third Party: ")}
					uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
					setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
					uploadedProfilesMap={uploadedProfilesMap}
					setUploadedProfilesMap={setUploadedProfilesMap}
					forceStartOnFirstPage={props.isFirstTimeThirdPartyCreation ?? false}
					onEnabledChange={() => {
						/**empty */
					}}
					entryPoint="lti-modal"
					quizType={props.quizType}
					{...buttonHandlers}
				/>
			) : (
				<EditQuizModalSkeleton />
			)}
		</CoreModal>
	);
}
